// Única fuente de verdad del vídeo del héroe de la landing (D-07, D-15). Lo leen el reproductor del
// héroe (CapaVideoHeroe.tsx), el antiguo HeroVideo.tsx mientras exista, el VideoObject de la home
// ((marketing)/page.tsx) y la entrada '' del sitemap: todos importan VIDEO_HERO, así que nunca se separan.
//
// Hay dos héroes (D-15): A abre con la animación de la biblioteca de B2 y sigue con la historia 2.0;
// B es un concepto propio. Los dos están publicados en public/video/ y viven en VIDEOS_HERO.
// Para cambiar de héroe se cambia SOLO VERSION_HERO (abajo): eso mueve a la vez el fichero, los
// subtítulos, el texto accesible, el JSON-LD y el sitemap (lo prueba version-hero.test.tsx).
// Los dos héroes tienen subtítulos (.vtt): se muestran siempre que se ve el vídeo, con sonido o sin él (18-34).

export type VersionHero = 'a' | 'b';

export interface VideoHero {
    ruta: string;
    poster: string;
    /** Los subtítulos en español, la misma ruta que el vídeo con `.vtt`. */
    subtitulos: string;
    duracionSegundos: number;
    ancho: number;
    alto: number;
    /**
     * `etiqueta` (schema `name`) y `descripcion` (schema `description`) son dos oraciones propias.
     * `etiquetaAccesible` es el `aria-label` del vídeo, una sola oración sin punto final que nombra
     * lo que se ve; se guarda literal en vez de derivarla de las otras dos.
     */
    etiqueta: string;
    descripcion: string;
    etiquetaAccesible: string;
}

// Sale de la entradilla de Heroe_B2: no añade ninguna afirmación nueva (UI-SPEC §Copywriting).
const DESCRIPCION_HERO =
    'Sesiones, notas, tests y documentos de una paciente de ejemplo, conectados en un solo tablero, y un asistente que cita la sesión de la que sale cada dato.';

// Medidas con ffprobe sobre el MP4 publicado (publicar-en-psicoguia.mjs, 18-25), nunca una estimación:
// hero-a.mp4 40.833333 s y hero-b.mp4 38.2 s. Alimentan el VideoObject y el sitemap de la home.
const DURACION_SEGUNDOS: Record<VersionHero, number> = { a: 40.83, b: 38.2 };

function videoDeVersion(version: VersionHero, etiquetaAccesible: string): VideoHero {
    const ruta = `/video/hero-${version}.mp4`;
    return {
        ruta,
        poster: `/video/hero-${version}-poster.jpg`,
        subtitulos: `/video/hero-${version}.vtt`,
        duracionSegundos: DURACION_SEGUNDOS[version],
        // 1,5 × la caja interior de la tarjeta B2 (1308×644): sin recorte en el cambio póster→vídeo (UI-SPEC §B.1).
        ancho: 1962,
        alto: 966,
        etiqueta: 'Demostración de TherapyRecap 2.0',
        descripcion: DESCRIPCION_HERO,
        etiquetaAccesible,
    };
}

// Cada texto accesible solo nombra lo que cuenta su guion (guionesVideo/hero-a.ts y hero-b.ts, D-16).
export const VIDEOS_HERO: Record<VersionHero, VideoHero> = {
    a: videoDeVersion(
        'a',
        'Vídeo de demostración de TherapyRecap 2.0 con la paciente de ejemplo Marta Ruiz: su memoria de sesiones, el tablero, una sesión con su nota, el asistente citando la sesión de la que sale cada dato y un informe',
    ),
    b: videoDeVersion(
        'b',
        'Vídeo de demostración de TherapyRecap 2.0: las sesiones, notas, tests y documentos de la paciente de ejemplo Marta Ruiz se ordenan en una sola memoria y el asistente encuentra el registro exacto que cita',
    ),
};

// A por defecto: continúa el propio B2 de la página, así el cambio del fotograma estático al vídeo
// no se nota (D-15, UI-SPEC §C.7). Para publicar el otro héroe se cambia solo esta constante.
export const VERSION_HERO: VersionHero = 'a';

export const VIDEO_HERO: VideoHero = VIDEOS_HERO[VERSION_HERO];
